<template>
  <v-dialog
    :value="value"
    max-width="560"
    scrollable
    persistent
    @input="$emit('input', $event)"
  >
    <v-card class="ds-dialog">
      <v-card-title class="ds-dialog-header">
        <v-icon color="white" class="mr-2" style="position: relative; z-index: 1">mdi-account-plus</v-icon>
        <span class="ds-dialog-title">Cadastrar novo paciente</span>
        <v-spacer />
        <v-btn icon color="white" class="close-btn" :disabled="saving" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="ds-dialog-body">
        <!-- Obrigatórios vazios ficam em vermelho ao tentar salvar (validate). -->
        <v-form ref="entryForm" @submit.prevent="save">
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-card-account-details-outline</v-icon>
                <span>Paciente</span>
              </div>
            </div>
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  ref="name"
                  v-model="form.name"
                  label="Nome completo *"
                  :rules="[required]"
                  :error-messages="serverErrors.name"
                  outlined
                  dense
                  hide-details="auto"
                  color="primary"
                  prepend-inner-icon="mdi-account"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.cpf"
                  v-mask="'###.###.###-##'"
                  label="CPF *"
                  :rules="[required, cpfRule]"
                  :error-messages="serverErrors.cpf"
                  outlined
                  dense
                  hide-details="auto"
                  color="primary"
                  prepend-inner-icon="mdi-card-account-details"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.phone"
                  v-mask="['(##) ####-####', '(##) #####-####']"
                  label="Telefone *"
                  :rules="[required, phoneRule]"
                  :error-messages="serverErrors.phone"
                  outlined
                  dense
                  hide-details="auto"
                  color="primary"
                  prepend-inner-icon="mdi-phone"
                />
              </v-col>
              <!-- Em quais clínicas o paciente será atendido — como no cadastro
                   completo. Médico solo não tem clínica: o campo não aparece. -->
              <v-col v-if="clinics.length" cols="12">
                <v-select
                  v-model="form.clinic_ids"
                  :items="clinics"
                  item-text="name"
                  item-value="id"
                  multiple
                  label="Clínicas *"
                  :rules="[required]"
                  :error-messages="serverErrors.clinic_ids"
                  :menu-props="{ offsetY: true, maxHeight: 260 }"
                  outlined
                  dense
                  hide-details="auto"
                  color="primary"
                  prepend-inner-icon="mdi-hospital-building"
                >
                  <template v-slot:selection="{ item, index }">
                    <span v-if="index === 0" class="quick-clinics__first">{{ item.name }}</span>
                    <span v-else-if="index === 1" class="quick-clinics__more">
                      +{{ form.clinic_ids.length - 1 }}
                    </span>
                  </template>
                </v-select>
              </v-col>
            </v-row>
          </section>

          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-account-details-outline</v-icon>
                <span>Outros dados</span>
                <span class="ds-form-section__optional">opcional</span>
              </div>
            </div>
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  v-model="form.email"
                  label="E-mail"
                  type="email"
                  :rules="[emailRule]"
                  :error-messages="serverErrors.email"
                  outlined
                  dense
                  hide-details="auto"
                  color="primary"
                  prepend-inner-icon="mdi-email-outline"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.birth_date"
                  label="Data de nascimento"
                  type="date"
                  :max="today"
                  :error-messages="serverErrors.birth_date"
                  outlined
                  dense
                  hide-details="auto"
                  color="primary"
                  prepend-inner-icon="mdi-cake-variant-outline"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="form.gender"
                  :items="genderOptions"
                  label="Gênero"
                  clearable
                  outlined
                  dense
                  hide-details="auto"
                  color="primary"
                  prepend-inner-icon="mdi-gender-male-female"
                />
              </v-col>
            </v-row>
          </section>
          <p class="quick-hint">
            O cadastro pode ser completado depois, no prontuário do paciente.
          </p>
        </v-form>
      </v-card-text>

      <v-card-actions class="ds-dialog-actions">
        <v-btn outlined color="primary" :disabled="saving" @click="close">
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-spacer />
        <v-btn color="primary" depressed :loading="saving" @click="save">
          <v-icon left>mdi-content-save</v-icon>
          Cadastrar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";
import { todayISO } from "@/utils/datetime";
import { required } from "@/utils/formRules";

/**
 * SALVIA-945 — cadastro rápido de paciente a partir do "Novo atendimento".
 *
 * Só CPF, nome e telefone são obrigatórios: o paciente chegou e a consulta
 * precisa começar; o resto se completa depois, no prontuário. Abre POR CIMA da
 * tela de novo atendimento e, ao salvar, devolve o paciente (`saved`) para ela
 * já mostrá-lo na lista.
 *
 * Usa o mesmo endpoint do cadastro completo (/api/register/create-patient-user):
 * se o CPF já existe, ele só vincula o paciente ao médico/clínica — o mesmo
 * comportamento do AddNewUserDialog —, e a resposta diz qual dos dois aconteceu
 * (`meta.created_patient`).
 */
const blankForm = () => ({
  name: "",
  cpf: "",
  phone: "",
  email: "",
  birth_date: "",
  gender: null,
  clinic_ids: [],
});

const onlyDigits = (v) => String(v || "").replace(/\D/g, "");

export default {
  name: "QuickPatientDialog",

  props: {
    value: { type: Boolean, default: false },
    /** Clínicas do médico ([{ id, name }]); vazio para médico solo. */
    clinics: { type: Array, default: () => [] },
    /** As marcadas na tela de novo atendimento — vêm pré-selecionadas. */
    defaultClinicIds: { type: Array, default: () => [] },
    /** O que estava digitado na busca — vira o nome inicial. */
    initialName: { type: String, default: "" },
  },

  emits: ["input", "saved"],

  setup() {
    const { success, info, erroDeRequisicao } = useSnackbar();
    return { success, info, erroDeRequisicao };
  },

  data() {
    return {
      form: blankForm(),
      serverErrors: {},
      saving: false,
      today: todayISO(),
      genderOptions: [
        { text: "Masculino", value: "Masculino" },
        { text: "Feminino", value: "Feminino" },
        { text: "Outro", value: "Outros" },
      ],
    };
  },

  watch: {
    value(open) {
      if (!open) return;
      this.form = {
        ...blankForm(),
        name: (this.initialName || "").trim(),
        clinic_ids: [...this.defaultClinicIds],
      };
      this.serverErrors = {};
      this.$nextTick(() => {
        this.$refs.entryForm && this.$refs.entryForm.resetValidation();
        setTimeout(() => this.$refs.name && this.$refs.name.focus(), 150);
      });
    },
    // Erro do servidor some assim que o campo é editado.
    "form.cpf"() { this.$delete(this.serverErrors, "cpf"); },
    "form.email"() { this.$delete(this.serverErrors, "email"); },
    "form.name"() { this.$delete(this.serverErrors, "name"); },
    "form.phone"() { this.$delete(this.serverErrors, "phone"); },
  },

  methods: {
    required,
    cpfRule(v) {
      return onlyDigits(v).length === 11 || "CPF deve ter 11 dígitos";
    },
    phoneRule(v) {
      const n = onlyDigits(v).length;
      return n === 10 || n === 11 || "Telefone com DDD (10 ou 11 dígitos)";
    },
    emailRule(v) {
      return !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "E-mail inválido";
    },

    close() {
      if (this.saving) return;
      this.$emit("input", false);
    },

    async save() {
      if (!this.$refs.entryForm.validate()) return;

      let doctorId = this.$store.getters["auth/userId"];
      if (!doctorId) {
        await this.$store.dispatch("auth/ensureAuth");
        doctorId = this.$store.getters["auth/userId"];
      }

      const phone = onlyDigits(this.form.phone);
      const payload = {
        name: this.form.name.trim(),
        cpf: onlyDigits(this.form.cpf),
        status: true,
        doctor_id: doctorId,
        phones: [{ number: phone, type: phone.length === 11 ? "celular" : "fixo", is_primary: true }],
        email: this.form.email ? this.form.email.trim() : null,
        birth_date: this.form.birth_date || null,
        gender: this.form.gender || null,
      };
      if (this.clinics.length) payload.clinic_ids = [...this.form.clinic_ids];

      this.saving = true;
      try {
        const { data } = await http.post("/api/register/create-patient-user", payload);
        const patient = data?.data;
        const created = data?.meta?.created_patient !== false;
        if (created) {
          this.success("Paciente cadastrado!");
        } else {
          // CPF já existia: o paciente foi vinculado, com o nome que já tinha.
          this.info(`CPF já cadastrado — ${patient?.name || "paciente"} foi vinculado(a) a você.`);
        }
        this.$emit("saved", { patient, created });
        this.$emit("input", false);
      } catch (err) {
        if (err.response?.status === 422 && err.response.data?.errors) {
          // Erro de campo vai para o campo (em vermelho), não para um aviso solto.
          const errors = err.response.data.errors;
          this.serverErrors = {
            name: errors.name,
            cpf: errors.cpf,
            email: errors.email,
            birth_date: errors.birth_date,
            phone: errors["phones.0.number"],
            clinic_ids: errors.clinic_ids || errors["clinic_ids.0"],
          };
          return;
        }
        this.erroDeRequisicao(err, err.response?.data?.message || "Não foi possível cadastrar o paciente.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.quick-clinics__first {
  max-width: 75%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.quick-clinics__more {
  margin-left: 6px;
  padding: 0 7px;
  border-radius: 999px;
  background: var(--ds-primary-surface);
  color: var(--ds-primary);
  font-size: 0.72rem;
  font-weight: 700;
}
.quick-hint {
  margin: 10px 2px 0;
  font-size: 12px;
  color: var(--ds-text-soft);
}
</style>
